import React, { useState, useEffect } from 'react'
import AdminLayout from '../../components/admin/AdminLayout'
import { useAuth } from '../../context/AuthContext'
import api from '../../utils/api'

interface Booking {
    _id: string;
    type: string;
    name: string;
    phone: string;
    email: string;
    date?: string;
    time?: string;
    guests?: number;
    message?: string;
    status: string;
    createdAt: string;
    property?: {
        _id: string;
        title: string;
        price: number;
        address: string;
    };
    project?: {
        _id: string;
        name: string;
        location: string;
        address: string;
        city: string;
        slug?: string;
    };
}

export default function AdminBookings() {
    const { token } = useAuth()
    const [bookings, setBookings] = useState<Booking[]>([])
    const [loading, setLoading] = useState(true)
    const [search, setSearch] = useState('')
    const [statusFilter, setStatusFilter] = useState('All')
    const [timeFilter, setTimeFilter] = useState('All')
    const [viewBooking, setViewBooking] = useState<Booking | null>(null)

    useEffect(() => {
        loadBookings()
    }, [token])

    const loadBookings = () => {
        if (!token) { setLoading(false); return }
        api.adminGetBookings(token)
            .then(data => { 
                if (Array.isArray(data)) {
                    // Filter out Inquiry types, keeping only site visits/tours
                    setBookings(data.filter(b => !b.type || !b.type.toLowerCase().includes('inquiry')))
                } 
            })
            .catch(console.error)
            .finally(() => setLoading(false))
    }

    const handleStatusChange = async (id: string, status: string) => {
        if (!token) return
        await api.adminUpdateBooking(token, id, { status })
        setBookings(prev => prev.map(b => b._id === id ? { ...b, status } : b))
    }

    const handleDelete = async (id: string) => {
        if (!token || !window.confirm('Are you sure you want to delete this tour request?')) return
        await api.adminDeleteBooking(token, id)
        setBookings(prev => prev.filter(b => b._id !== id))
    }

    const getPropertyName = (b: Booking) => {
        if (b.project?.name) return b.project.name
        if (b.property?.title) return b.property.title
        return 'N/A'
    }

    const getPropertyLocation = (b: Booking) => {
        if (b.project?.location) return b.project.location
        if (b.property?.address) return b.property.address
        return ''
    }

    const filtered = bookings.filter(b => {
        const matchSearch = b.name.toLowerCase().includes(search.toLowerCase()) ||
            b.phone.includes(search) ||
            getPropertyName(b).toLowerCase().includes(search.toLowerCase())
        
        const matchStatus = statusFilter === 'All' || b.status === statusFilter

        let matchTime = true
        if (timeFilter !== 'All') {
            const bDate = new Date(b.createdAt)
            const now = new Date()
            const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
            
            if (timeFilter === 'Today') {
                matchTime = bDate >= today
            } else if (timeFilter === 'Yesterday') {
                const yesterday = new Date(today)
                yesterday.setDate(yesterday.getDate() - 1)
                matchTime = bDate >= yesterday && bDate < today
            } else if (timeFilter === 'Last 7 Days') {
                const last7 = new Date(today)
                last7.setDate(last7.getDate() - 7)
                matchTime = bDate >= last7
            } else if (timeFilter === 'Last 30 Days') {
                const last30 = new Date(today)
                last30.setDate(last30.getDate() - 30)
                matchTime = bDate >= last30
            } else if (timeFilter === 'This Month') {
                const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)
                matchTime = bDate >= firstOfMonth
            }
        }

        return matchSearch && matchStatus && matchTime
    })

    const counts = {
        all: bookings.length,
        pending: bookings.filter(b => b.status === 'Pending').length,
        completed: bookings.filter(b => b.status === 'Completed').length,
        cancelled: bookings.filter(b => b.status === 'Cancelled').length,
    }

    return (
        <AdminLayout>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                <div>
                    <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">Site Visit Bookings</h1>
                    <p className="text-slate-500 text-base font-medium">Manage and track property tour requests from potential clients.</p>
                </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
                {[
                    { label: 'Total Bookings', count: counts.all, icon: 'fa-calendar-check', color: 'text-blue-600', bg: 'bg-blue-50 dark:bg-blue-900/20', filter: 'All' },
                    { label: 'Pending Response', count: counts.pending, icon: 'fa-clock', color: 'text-amber-600', bg: 'bg-amber-50 dark:bg-amber-900/20', filter: 'Pending' },
                    { label: 'Completed Tours', count: counts.completed, icon: 'fa-circle-check', color: 'text-emerald-600', bg: 'bg-emerald-50 dark:bg-emerald-900/20', filter: 'Completed' },
                    { label: 'Cancelled', count: counts.cancelled, icon: 'fa-circle-xmark', color: 'text-rose-600', bg: 'bg-rose-50 dark:bg-rose-900/20', filter: 'Cancelled' },
                ].map((s, i) => (
                    <button 
                        key={i}
                        onClick={() => setStatusFilter(s.filter)}
                        className={`relative group p-6 rounded-2xl border transition-all text-left ${
                            statusFilter === s.filter 
                            ? 'bg-white dark:bg-slate-900 border-blue-500 shadow-xl shadow-blue-500/10' 
                            : 'bg-white dark:bg-slate-900 border-slate-100 dark:border-slate-800 hover:border-blue-300'
                        }`}
                    >
                        <div className={`w-12 h-12 rounded-xl ${s.bg} ${s.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                            <i className={`fa-solid ${s.icon} text-xl`}></i>
                        </div>
                        <p className="text-xs font-bold text-slate-500 mb-1 uppercase tracking-wider">{s.label}</p>
                        <h4 className="text-3xl font-bold text-slate-900 dark:text-white leading-none tabular-nums">{s.count}</h4>
                    </button>
                ))}
            </div>

            <div className="bg-white dark:bg-slate-900 rounded-md border border-blue-900/10 dark:border-slate-800 shadow-2xl shadow-slate-200/50 dark:shadow-none overflow-hidden mb-12">
                {/* Modern Search & Filter Bar */}
                <div className="p-8 border-b border-slate-100 dark:border-slate-800 flex flex-col md:flex-row justify-between items-center gap-6 bg-white/50 dark:bg-slate-800/30">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-blue-500 text-white flex items-center justify-center shadow-lg shadow-blue-500/20">
                            <i className="fa-solid fa-layer-group text-xl"></i>
                        </div>
                        <div>
                            <h3 className="text-xl font-bold text-slate-900 dark:text-white leading-tight">Bookings List</h3>
                            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{filtered.length} requests found</p>
                        </div>
                    </div>
                    
                    <div className="flex flex-col md:flex-row items-center gap-4 w-full md:w-auto">
                        <div className="relative group w-full md:w-[180px]">
                            <select
                                className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl py-3 px-4 text-sm font-semibold text-slate-700 dark:text-slate-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all appearance-none cursor-pointer"
                                value={statusFilter}
                                onChange={e => setStatusFilter(e.target.value)}
                            >
                                <option value="All">All Statuses</option>
                                <option value="Pending">Pending</option>
                                <option value="Completed">Completed</option>
                                <option value="Cancelled">Cancelled</option>
                            </select>
                            <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none opacity-40">
                                <i className="fa-solid fa-chevron-down text-xs"></i>
                            </div>
                        </div>

                        <div className="relative group w-full md:w-[180px]">
                            <select
                                className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl py-3 px-4 text-sm font-semibold text-slate-700 dark:text-slate-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all appearance-none cursor-pointer"
                                value={timeFilter}
                                onChange={e => setTimeFilter(e.target.value)}
                            >
                                <option value="All">All Time</option>
                                <option value="Today">Today</option>
                                <option value="Yesterday">Yesterday</option>
                                <option value="Last 7 Days">Last 7 Days</option>
                                <option value="Last 30 Days">Last 30 Days</option>
                                <option value="This Month">This Month</option>
                            </select>
                            <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none opacity-40">
                                <i className="fa-solid fa-chevron-down text-xs"></i>
                            </div>
                        </div>

                        <div className="relative group w-full md:w-[350px]">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                <i className="fa-solid fa-magnifying-glass text-slate-400 group-focus-within:text-blue-500 transition-colors"></i>
                            </div>
                             <input 
                                type="text" 
                                className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl py-3 pl-12 pr-4 text-sm font-medium text-slate-700 dark:text-slate-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 placeholder:text-slate-400 transition-all"
                                placeholder="Search client or property..."
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                            />
                        </div>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    {loading ? (
                        <div className="p-32 text-center">
                            <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
                             <p className="text-slate-500 font-semibold text-sm">Loading bookings...</p>
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="p-32 text-center">
                            <div className="w-20 h-20 bg-slate-50 dark:bg-slate-800 rounded-2xl flex items-center justify-center mx-auto mb-6 border border-slate-100 dark:border-slate-700">
                                <i className="fa-solid fa-calendar-xmark text-3xl text-slate-300"></i>
                            </div>
                            <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">No Bookings Found</h3>
                            <p className="text-slate-500 text-sm max-w-xs mx-auto font-medium">We couldn't find any bookings matching your current filters.</p>
                        </div>
                    ) : (
                        <table className="w-full text-left">
                            <thead>
                                <tr className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-100 dark:border-slate-800">
                                    <th className="px-8 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Client Details</th>
                                    <th className="px-8 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Schedule</th>
                                    <th className="px-8 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Contact</th>
                                    <th className="px-8 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Status</th>
                                    <th className="px-8 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                {filtered.map(b => (
                                    <tr key={b._id} className="group hover:bg-white/50 dark:hover:bg-slate-800/20 transition-all">
                                        <td className="px-8 py-5">
                                            <div className="flex items-center gap-4">
                                                <div className="w-11 h-11 rounded-xl bg-blue-600 flex items-center justify-center shadow-lg shadow-blue-600/20">
                                                    <span className="text-white font-bold text-lg">{b.name.charAt(0).toUpperCase()}</span>
                                                </div>
                                                <div>
                                                    <h4 className="font-bold text-slate-900 dark:text-white text-base leading-tight">{b.name}</h4>
                                                    <div className="flex items-center gap-3 mt-1 text-xs font-medium text-slate-500">
                                                        <span className="flex items-center gap-1.5"><i className="fa-solid fa-phone text-[10px]"></i> {b.phone}</span>
                                                        {b.email && <span className="flex items-center gap-1.5"><i className="fa-solid fa-envelope text-[10px]"></i> {b.email}</span>}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>

                                        <td className="px-8 py-5">
                                            <div className="flex items-center gap-3">
                                                <div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-2 px-3 border border-slate-100 dark:border-slate-700 text-center shadow-sm min-w-[60px]">
                                                    <div className="text-base font-bold text-slate-900 dark:text-white leading-none">
                                                        {b.date ? new Date(b.date).getDate() : '—'}
                                                    </div>
                                                    <div className="text-[10px] font-bold text-slate-400 uppercase mt-1">
                                                        {b.date ? new Date(b.date).toLocaleDateString('en-US', { month: 'short' }) : ''}
                                                    </div>
                                                </div>
                                                <div>
                                                    <div className="text-sm font-bold text-slate-900 dark:text-white">{b.time || 'Schedule pending'}</div>
                                                    <div className="text-xs font-medium text-slate-500 mt-0.5">{b.type}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-8 py-5">
                                            <div className="flex items-center gap-2">
                                                <a 
                                                    href={`tel:${b.phone}`}
                                                    className="w-9 h-9 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 hover:bg-blue-600 hover:text-white transition-all flex items-center justify-center shadow-sm border border-blue-100"
                                                    title="Call client"
                                                >
                                                    <i className="fa-solid fa-phone text-xs"></i>
                                                </a>
                                                <a 
                                                    href={`https://wa.me/91${b.phone.replace(/\D/g, '')}?text=Hi ${b.name}, I'm calling from My Property Guru regarding your inquiry for ${getPropertyName(b)}.`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="w-9 h-9 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-600 hover:text-white transition-all flex items-center justify-center shadow-sm border border-emerald-100"
                                                    title="WhatsApp client"
                                                >
                                                    <i className="fa-brands fa-whatsapp text-lg"></i>
                                                </a>
                                                {(b.project?._id || b.property?._id) && (
                                                    <a 
                                                        href={b.project?._id ? `/project-details/${b.project.slug || b.project._id}` : `/property-details/${b.property?._id}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="w-9 h-9 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-600 hover:text-white transition-all flex items-center justify-center shadow-sm border border-slate-200"
                                                        title="View project"
                                                    >
                                                        <i className="fa-solid fa-eye text-xs"></i>
                                                    </a>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-8 py-5">
                                            <div className="relative group/sel w-32">
                                                <select
                                                    className={`appearance-none font-bold text-xs px-3 py-2 rounded-lg border-none focus:ring-2 focus:ring-blue-500 transition-all cursor-pointer w-full ${
                                                        b.status === 'Pending' ? 'bg-amber-100 text-amber-700' :
                                                        b.status === 'Completed' ? 'bg-emerald-100 text-emerald-700' : 
                                                        'bg-rose-100 text-rose-700'
                                                    }`}
                                                    value={b.status}
                                                    onChange={e => handleStatusChange(b._id, e.target.value)}
                                                >
                                                    <option value="Pending">Pending</option>
                                                    <option value="Completed">Completed</option>
                                                    <option value="Cancelled">Cancelled</option>
                                                </select>
                                                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none opacity-40">
                                                    <i className="fa-solid fa-chevron-down text-[8px]"></i>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-8 py-5 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <button 
                                                    className="w-9 h-9 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-blue-600 hover:text-white transition-all flex items-center justify-center shadow-sm border border-slate-200"
                                                    onClick={() => setViewBooking(b)}
                                                    title="View Details"
                                                >
                                                    <i className="fa-solid fa-expand text-xs"></i>
                                                </button>
                                                <button 
                                                    className="w-9 h-9 rounded-lg bg-rose-50 dark:bg-rose-900/20 text-rose-600 dark:text-rose-400 hover:bg-rose-600 hover:text-white transition-all flex items-center justify-center shadow-sm border border-rose-100"
                                                    onClick={() => handleDelete(b._id)}
                                                    title="Delete Booking"
                                                >
                                                    <i className="fa-solid fa-trash-can text-xs"></i>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {/* Premium Details Overlay */}
            {viewBooking && (
                <div className="fixed inset-0 z-[1100] flex items-center justify-center px-4 overflow-hidden">
                    <div className="absolute inset-0 bg-slate-900/80 backdrop-blur-sm animate-in fade-in duration-300" onClick={() => setViewBooking(null)}></div>
                    <div className="relative w-full max-w-2xl bg-white dark:bg-slate-900 rounded-3xl border border-slate-100 dark:border-slate-800 shadow-2xl animate-in zoom-in-95 duration-300 overflow-hidden">
                        <div className="relative h-32 bg-blue-600 flex flex-col justify-end p-8">
                                <button 
                                onClick={() => setViewBooking(null)}
                                className="absolute top-6 right-6 w-10 h-10 rounded-full bg-white/20 text-white hover:bg-white/40 transition-colors flex items-center justify-center"
                            >
                                <i className="fa-solid fa-xmark"></i>
                            </button>
                            <div className="flex items-center gap-6 translate-y-8">
                                <div className="w-20 h-20 rounded-2xl bg-white dark:bg-slate-900 p-1 shadow-2xl">
                                    <div className="w-full h-full rounded-xl bg-blue-600 flex items-center justify-center">
                                        <span className="text-3xl font-bold text-white">
                                            {viewBooking.name.charAt(0).toUpperCase()}
                                        </span>
                                    </div>
                                </div>
                                <div className="mb-2">
                                    <h2 className="text-2xl font-bold text-white leading-tight">{viewBooking.name}</h2>
                                    <div className={`mt-1 inline-flex px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                                        viewBooking.status === 'Pending' ? 'bg-amber-400 text-amber-900' :
                                        viewBooking.status === 'Completed' ? 'bg-emerald-400 text-emerald-900' : 
                                        'bg-rose-400 text-rose-900'
                                    }`}>
                                        {viewBooking.status} Request
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="p-8 pt-12">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-4">
                                <div className="space-y-4">
                                    <div>
                                        <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Property Details</p>
                                        <h4 className="font-bold text-slate-900 dark:text-white text-base tracking-tight leading-snug">{getPropertyName(viewBooking)}</h4>
                                        <div className="flex items-center gap-2 mt-2">
                                            <i className="fa-solid fa-location-dot text-slate-300 text-xs"></i>
                                            <span className="text-sm font-semibold text-slate-500">{getPropertyLocation(viewBooking)}</span>
                                        </div>
                                    </div>
                                    
                                    <div className="grid grid-cols-2 gap-4 pt-4 border-t border-slate-100 dark:border-slate-800">
                                        <div>
                                            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Visit Date</p>
                                            <p className="text-sm font-bold text-slate-900 dark:text-white">
                                                {viewBooking.date ? new Date(viewBooking.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'Flexible'}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Time Slot</p>
                                            <p className="text-sm font-bold text-slate-900 dark:text-white">{viewBooking.time || 'Not specified'}</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <div>
                                        <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Contact Information</p>
                                        <div className="space-y-2">
                                            <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-700">
                                                <div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-600 flex items-center justify-center border border-blue-100">
                                                    <i className="fa-solid fa-phone text-[10px]"></i>
                                                </div>
                                                <span className="text-sm font-bold text-slate-700 dark:text-slate-200">{viewBooking.phone}</span>
                                            </div>
                                            {viewBooking.email && (
                                                <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-700">
                                                    <div className="w-8 h-8 rounded-lg bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 flex items-center justify-center border border-emerald-100">
                                                        <i className="fa-solid fa-envelope text-[10px]"></i>
                                                    </div>
                                                    <span className="text-sm font-bold text-slate-700 dark:text-slate-200 truncate">{viewBooking.email}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div>
                                        <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Request Type</p>
                                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-100 dark:bg-slate-800 text-xs font-bold text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700">
                                            <i className="fa-solid fa-tag text-slate-400"></i>
                                            {viewBooking.type}
                                        </div>
                                    </div>
                                </div>

                                {viewBooking.message && (
                                    <div className="col-span-2 pt-6 border-t border-slate-100 dark:border-slate-800">
                                        <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-2">
                                            <i className="fa-solid fa-message text-slate-300"></i>
                                            Client Message
                                        </p>
                                        <div className="bg-slate-50 dark:bg-slate-800/80 rounded-2xl p-5 text-sm text-slate-700 dark:text-slate-300 font-medium leading-relaxed border border-slate-100 dark:border-slate-700">
                                            "{viewBooking.message}"
                                        </div>
                                    </div>
                                )}
                            </div>

                             <div className="mt-8 p-6 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-100 dark:border-slate-700 flex flex-wrap items-center justify-between gap-6">
                                <div>
                                    <h5 className="text-sm font-bold text-slate-900 dark:text-white">Quick Actions</h5>
                                    <p className="text-xs font-medium text-slate-500 mt-1">Direct communication with the client</p>
                                </div>
                                <div className="flex items-center gap-4">
                                    <a 
                                        href={`tel:${viewBooking.phone}`}
                                         className="px-5 py-2.5 rounded-xl bg-white dark:bg-slate-900 text-slate-900 dark:text-white font-bold text-xs border border-slate-200 dark:border-slate-700 hover:bg-blue-600 hover:text-white transition-all flex items-center gap-2 shadow-sm"
                                    >
                                        <i className="fa-solid fa-phone"></i> Voice Call
                                    </a>
                                    <a 
                                        href={`https://wa.me/91${viewBooking.phone.replace(/\D/g, '')}?text=Hi ${viewBooking.name}, I'm calling from My Property Guru regarding your inquiry for ${getPropertyName(viewBooking)}.`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="px-5 py-2.5 rounded-xl bg-emerald-600 text-white font-bold text-xs hover:bg-emerald-700 transition-all flex items-center gap-2 shadow-lg shadow-emerald-600/20"
                                    >
                                        <i className="fa-brands fa-whatsapp text-sm"></i> WhatsApp
                                    </a>
                                </div>
                             </div>

                              <div className="mt-8 flex items-center justify-between">
                                <button 
                                    className="px-6 py-3 rounded-xl bg-rose-50 text-rose-600 font-bold text-xs hover:bg-rose-600 hover:text-white transition-all shadow-sm border border-rose-100"
                                    onClick={() => {
                                        if (window.confirm('Are you sure you want to delete this booking permanently?')) {
                                            handleDelete(viewBooking._id);
                                            setViewBooking(null);
                                        }
                                    }}
                                >
                                    Delete Booking
                                </button>
                                <button 
                                    className="px-8 py-3 rounded-xl bg-slate-900 text-white hover:bg-slate-800 font-bold text-xs transition-all shadow-xl active:scale-95"
                                    onClick={() => setViewBooking(null)}
                                >
                                    Close Details
                                </button>
                             </div>
                        </div>
                    </div>
                </div>
            )}
        </AdminLayout>
    )
}


